import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  ServerError,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { Operation } from '@apollo/client/link/core'
import { onError } from '@apollo/client/link/error'
import { relayStylePagination } from '@apollo/client/utilities'
import { getApiUrl, getGraphqlUrl } from 'utils/api/apiHelpers'
import { log } from 'utils/errorLogger'
import { getUserCookieValues, logout } from '../userAuth'
import { EXCLUDED_BATCH_OPERATIONS } from './excludedBatchOperations'
import carrierCallListForShipmentFieldPolicy from './fieldPolicies/carrierCallListForShipment'
import shipmentRecommendationsFieldPolicy from './fieldPolicies/shipmentRecommendations'

// Setup Apollo's Auth Link
const authLink = setContext((_, { headers }) => {
  const userData = getUserCookieValues()

  const { authToken, email, userId } = userData || {}

  return {
    headers: {
      ...headers,
      'x-user-email': email,
      'x-user-token': authToken,
      'x-user-id': userId,
      'x-rogers-revision': __ROGERS_VERSION__,
    },
  }
})

const batchLink = new BatchHttpLink({
  uri:
    getGraphqlUrl('MARKETPLACE') || `${getApiUrl('MARKETPLACE').replace('/stark', '')}/marketplace`,
  batchMax: 20,
  batchInterval: 100,
  fetch: (uri, options) => {
    const { body } = options || {}

    const operationNames = JSON.parse(body as string).map(
      (operation: Operation) => operation.operationName
    )

    return fetch(`${uri}?ops=${operationNames}`, options)
  },
})

const httpLink = createHttpLink({
  uri: operation => {
    const baseUrl =
      getGraphqlUrl('MARKETPLACE') ||
      `${getApiUrl('MARKETPLACE').replace('/stark', '')}/marketplace`
    const operationName = operation.operationName || 'anonymous'
    return `${baseUrl}?op=${operationName}`
  },
})

const fmsHttpLink = createHttpLink({
  uri: operation => {
    const baseUrl = getGraphqlUrl('FMS')
    const operationName = operation.operationName || 'anonymous'
    return `${baseUrl}?op=${operationName}`
  },
})

const tmsHttpLink = createHttpLink({
  uri: operation => {
    const baseUrl = getGraphqlUrl('TMS')
    const operationName = operation.operationName || 'anonymous'
    return `${baseUrl}?op=${operationName}`
  },
})

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (networkError && (networkError as ServerError).statusCode === 401) {
    logout()
  } else {
    const error = graphQLErrors
      ?.map(
        ({ locations, message, path }) =>
          `Message: ${message}, Location: ${locations}, Path: ${path}`
      )
      .join(' , ')

    log('graphql', graphQLErrors ? new Error(error) : networkError ?? undefined, { operation })
  }
})

export const marketplaceBatchClient = new ApolloClient({
  link: authLink.split(
    operation => EXCLUDED_BATCH_OPERATIONS.includes(operation.operationName),
    ApolloLink.from([errorLink, httpLink]),
    ApolloLink.from([errorLink, batchLink])
  ),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          carrierCallListForShipment: carrierCallListForShipmentFieldPolicy,
          shipmentListForCarrier: relayStylePagination(['carrierId']),
        },
      },
      Shipment: {
        fields: {
          equipmentAttributes: {
            merge: false,
          },
          characteristics: {
            merge: true,
          },
        },
      },
    },
  }),
  connectToDevTools: !__PROD__,
})

export const marketplaceClient = new ApolloClient({
  link: authLink.concat(ApolloLink.from([errorLink, httpLink])),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          carrierCallListForShipment: carrierCallListForShipmentFieldPolicy,
          requestForProposalLanes: relayStylePagination(['id']),
          shipmentListForCarrier: relayStylePagination(['carrierId']),
          sourcedCarrierRates: relayStylePagination(['shipperRateIds']),
          shipperRates: relayStylePagination([
            'pickupLocation',
            'deliveryLocation',
            'shipperRateId',
            'carrierSalesRepIds',
            'sourceStatuses',
            'shipperApplicationIds',
            'carrierApplicationIds',
            'dropTypes',
            'equipmentIds',
            'sortBy',
            'sortDirection',
            'shouldSource',
          ]),
        },
      },
      CarrierCertification: {
        keyFields: ['certificationId'],
      },
      Shipment: {
        fields: {
          equipmentAttributes: {
            merge: false,
          },
          characteristics: {
            merge: true,
          },
        },
      },
    },
  }),
  connectToDevTools: !__PROD__,
})

export const fmsGraphQLClient = new ApolloClient({
  link: authLink.concat(ApolloLink.from([errorLink, fmsHttpLink])),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          shipmentRecommendations: shipmentRecommendationsFieldPolicy,
        },
      },
      RecommendedShipments: {
        fields: {
          shipments: {
            keyArgs: false,
          },
        },
      },
    },
  }),
  connectToDevTools: !__PROD__,
})

export const tmsGraphQLClient = new ApolloClient({
  link: authLink.concat(ApolloLink.from([errorLink, tmsHttpLink])),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          shipperCarriers: relayStylePagination(),
        },
      },
    },
  }),
  connectToDevTools: !__PROD__,
})
